import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export interface TemplateMailItem {
  uuid: string
  code: string
  externalReference: string
  description: string
  dateCreated: string
  dateUpdated: string
}

export interface TemplateMailInput {
  code: string
  externalReference: string
  description: string
}

type StoreResult<T> = { ok: true; data: T } | { ok: false; message: string }

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    if (body?.message) return Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
  } catch {
    // ignore
  }
  return fallback
}

export const useTemplateMailAdminStore = defineStore('templateMailAdmin', () => {
  const suiteStore = useSuiteStore()
  const templates = ref<TemplateMailItem[]>([])
  const isLoading = ref(false)

  async function fetchAll(): Promise<TemplateMailItem[]> {
    isLoading.value = true
    try {
      const res = await suiteStore.suiteFetch('template-mail', {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json = (await res.json()) as TemplateMailItem[]
        templates.value = (json ?? []).slice().sort((a, b) => a.code.localeCompare(b.code))
        return templates.value
      }
    } catch (e) {
      console.error('Erreur récupération template-mail', e)
    } finally {
      isLoading.value = false
    }
    return []
  }

  async function create(input: TemplateMailInput): Promise<StoreResult<TemplateMailItem>> {
    const res = await suiteStore.suiteFetch('template-mail', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as TemplateMailItem
      templates.value.push(data)
      templates.value.sort((a, b) => a.code.localeCompare(b.code))
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la création du template')
      : 'Erreur lors de la création du template'
    return { ok: false, message }
  }

  async function update(uuid: string, input: Partial<TemplateMailInput>): Promise<StoreResult<TemplateMailItem>> {
    const res = await suiteStore.suiteFetch(`template-mail/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as TemplateMailItem
      const idx = templates.value.findIndex((t) => t.uuid === uuid)
      if (idx >= 0) templates.value[idx] = data
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la mise à jour')
      : 'Erreur lors de la mise à jour'
    return { ok: false, message }
  }

  async function remove(uuid: string): Promise<StoreResult<true>> {
    const res = await suiteStore.suiteFetch(`template-mail/${uuid}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      templates.value = templates.value.filter((t) => t.uuid !== uuid)
      return { ok: true, data: true }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Suppression impossible')
      : 'Suppression impossible'
    return { ok: false, message }
  }

  return { templates, isLoading, fetchAll, create, update, remove }
})
